/**
 * FunnelChart — Horizontal bar chart showing prospect funnel stages
 * with summary cards and drop-off percentages between stages.
 */

import { useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowDown } from 'lucide-react';
import { useFunnel } from '@/lib/api/hooks/useAnalytics';

interface FunnelChartProps {
  dateFrom: string;
  dateTo: string;
  onStageClick?: (stageName: string) => void;
  highlightId?: string;
}

// Colors for each funnel stage (Lead → Closed Won)
const STAGE_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#10b981'];

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

export function FunnelChart({ dateFrom, dateTo, onStageClick, highlightId }: FunnelChartProps) {
  const { data, isLoading, error } = useFunnel({ date_from: dateFrom, date_to: dateTo });

  useEffect(() => {
    if (!highlightId || isLoading) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`section-${highlightId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 3000);
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightId, isLoading]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-4 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {error ? `Failed to load funnel data: ${(error as Error).message}` : 'No funnel data available.'}
      </div>
    );
  }

  const { stages, summary } = data;
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Total Leads</p>
            <p className="text-3xl font-bold leading-tight">{summary.total_leads.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card id="section-close_rate">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Close Rate</p>
            <p className="text-3xl font-bold leading-tight">{summary.close_rate}%</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Conversion Rate</p>
            <p className="text-3xl font-bold leading-tight">{summary.conversion_rate}%</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Revenue</p>
            <p className="text-3xl font-bold leading-tight">{formatCurrency(summary.total_revenue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prospect Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.total_leads === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No prospects found for the selected date range.
            </p>
          ) : (
            <div className="space-y-0">
              {stages.map((stage, index) => (
                <div key={stage.name}>
                  {/* Drop-off label between stages */}
                  {index > 0 && stage.drop_off_pct !== null && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground pl-32 py-0.5">
                      <ArrowDown className="h-3 w-3 text-red-400" />
                      <span className="text-red-500 font-medium">{stage.drop_off_pct}% drop-off</span>
                    </div>
                  )}

                  {/* Stage row */}
                  <div
                    id={`section-${stage.name.toLowerCase().replace(/\s+/g, '_')}`}
                    className={`flex items-center gap-3 py-1 rounded ${onStageClick && stage.count > 0 ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                    onClick={() => onStageClick && stage.count > 0 && onStageClick(stage.name.toLowerCase().replace(' ', '_'))}
                    title={onStageClick && stage.count > 0 ? `View ${stage.name} prospects` : undefined}
                  >
                    {/* Stage name */}
                    <span className="w-28 text-sm text-right text-muted-foreground shrink-0">
                      {stage.name}
                    </span>

                    {/* Bar */}
                    <div className="flex-1 flex items-center gap-2">
                      <div
                        className="h-8 rounded-sm transition-all"
                        style={{
                          width: `${Math.max((stage.count / maxCount) * 100, stage.count > 0 ? 2 : 0)}%`,
                          backgroundColor: STAGE_COLORS[index],
                          minWidth: stage.count > 0 ? '4px' : '0',
                        }}
                      />
                      <span className="text-sm font-semibold tabular-nums">
                        {stage.count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recharts version — hidden accessibility chart for screen readers / tooltips */}
      {summary.total_leads > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Stage Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={stages}
                margin={{ top: 4, right: 40, left: 72, bottom: 4 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                  width={72}
                />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Prospects']}
                  cursor={{ fill: 'hsl(var(--muted))' }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stages.map((_, i) => (
                    <Cell key={i} fill={STAGE_COLORS[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
